"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useI18n, type Locale } from "@/i18n"

const LOCALES: Locale[] = ["en", "de"]

/** `onDark`: dark-surface variant (ink footer) — the default light styling
 * (ink-2 text, cream hover) is invisible on an ink background. */
export function LanguageToggle({ onDark = false }: { onDark?: boolean }) {
  const { locale, setLocale, t } = useI18n()
  return (
    <div
      role="group"
      aria-label={t("language.label")}
      className={`inline-flex overflow-hidden rounded-bb-full border ${
        onDark ? "border-paper/25" : "border-line"
      }`}
    >
      {/* flex-1 + centered content on each button: when the pill is
          stretched by a flex-col parent (LeftRail sidebar), the two buttons
          split it evenly instead of leaving a lopsided empty area inside
          the border after "DE"; in inline contexts (landing header) the
          pill still sizes to content. */}
      {LOCALES.map(code => (
        <button
          key={code}
          type="button"
          aria-pressed={locale === code}
          onClick={() => setLocale(code)}
          className={`inline-flex min-h-11 min-w-11 flex-1 items-center justify-center px-3 text-xs font-semibold transition-colors ${
            locale === code
              ? onDark
                ? "bg-lime text-ink"
                : "bg-green text-paper"
              : onDark
                ? "bg-transparent text-paper/60 hover:bg-paper/10 hover:text-paper"
                : "bg-transparent text-ink-2 hover:bg-cream"
          }`}
        >
          {t(`language.${code}`)}
        </button>
      ))}
    </div>
  )
}
